/**
 * `frozenCopy` — the one step a rows-side store takes before it keeps a value
 * a read will hand out (a row's attribute and metadata bags, an alias
 * instance's props and overrides, a cell's value): the value,
 * deeply frozen, so every reader shares one value that no read can write, and
 * a write path produces a new frozen value instead of editing the old one.
 *
 * It copies rather than freezing in place: the caller keeps the object it
 * handed in as it was (a wire record, a fixture, an edit it is still
 * building). Plain objects and arrays are copied and frozen at every depth;
 * everything else is kept as it is — a primitive, a function, and a class
 * instance (a `@tento/chrono` `DateTime` in an event keeps lazy caches in its
 * fields and cannot be frozen yet). A value that is already deeply frozen is
 * answered as it is, so a store re-keeping its own value (`patch` over the
 * previous record) keeps its identity.
 */

import type { Json, JsonObject } from "./json.ts";
import type { CellValue } from "./dtype.ts";

/** The frozen empty bag a read answers for a row with none. */
export const EMPTY_FROZEN_OBJECT: JsonObject = Object.freeze({});

/** `value` deeply frozen (see the module note): itself when it already is, else a frozen copy. */
export function frozenCopy(value: JsonObject): JsonObject;
export function frozenCopy(value: Json): Json;
export function frozenCopy(value: CellValue): CellValue;
/** A list of records (a row's option contributions, …): each element frozen as above. */
export function frozenCopy<T extends object>(value: readonly T[]): readonly T[];
export function frozenCopy(value: unknown): unknown {
  if (isDeepFrozen(value)) return value;
  return copy(value);
}

/** A plain object or array (own or `null` prototype) — what `frozenCopy` copies. */
function isPlain(value: object): boolean {
  if (Array.isArray(value)) return true;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** Whether every plain object and array reachable from `value` is frozen. */
function isDeepFrozen(value: unknown): boolean {
  if (typeof value !== "object" || value === null || !isPlain(value)) return true;
  if (!Object.isFrozen(value)) return false;
  return Object.values(value).every(isDeepFrozen);
}

function copy(value: unknown): unknown {
  if (Array.isArray(value)) return Object.freeze(value.map(copy));
  if (typeof value !== "object" || value === null || !isPlain(value)) return value;
  const out: { [key: string]: unknown } = {};
  // `Object.keys` (the same own enumerable keys, in the same order, as
  // `Object.entries`) and no `[key, value]` pair per field: a store copies
  // every cell it keeps, and the pairs were the copy's largest allocation.
  for (const key of Object.keys(value)) out[key] = copy(Reflect.get(value, key));
  return Object.freeze(out);
}
